import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { Icon } from '@/components/Icon/Icon';
import { InviteForm } from '@/components/InviteForm/InviteForm';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PeopleList } from '@/components/PeopleList/PeopleList';
import { PortfolioForm, type PortfolioChange } from '@/components/PortfolioForm/PortfolioForm';
import { RolesForm } from '@/components/RolesForm/RolesForm';
import { Spinner } from '@/components/Spinner/Spinner';
import {
  ASSIGN_TENANT_MANAGER,
  PORTFOLIOS_QUERY,
  UNASSIGN_TENANT_MANAGER,
  type PortfoliosResult,
} from '@/graphql/brokerages';
import { ME_QUERY, type MeQueryResult } from '@/graphql/keystone';
import {
  ADMIT_STAFF,
  ASSIGN_PLATFORM_ROLE,
  INTERNAL_ACCOUNTS_QUERY,
  REMOVE_STAFF,
  UNASSIGN_PLATFORM_ROLE,
  type InternalAccount,
  type InternalAccountsResult,
  type PlatformRole,
} from '@/graphql/staff';
import {
  portfolioAccessOf,
  portfolioGrants,
  type PortfolioAccess,
} from '@/lib/brokerage-permissions';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { personLabel, ROLES, roleChanges } from '@/lib/staff';
import { useResourceAccess } from '@/lib/use-resource-access';
import { useToast } from '@/toast/useToast';

import styles from './PeopleContainer.module.css';

/**
 * `/people`: who can use the internal portal and what each may do. Read with
 * `internal_accounts` Read; inviting, changing roles and removing need Full.
 * Keystone refuses changes to your own access and removing the last Admin.
 */
export function PeopleContainer() {
  const toast = useToast();
  const { identity } = useAuth();
  const { access, refetch: recheckAccess } = useResourceAccess('internal_accounts');
  const canRead = access === 'read' || access === 'full';
  const canEdit = access === 'full';
  const { access: portfolioAccess } = useResourceAccess('managed_tenants');
  const canAssignBrokerages = canEdit && portfolioAccess === 'full';

  const me = useQuery<MeQueryResult>(ME_QUERY, { skip: !canRead });
  const accounts = useQuery<InternalAccountsResult>(INTERNAL_ACCOUNTS_QUERY, {
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  const refetch = { refetchQueries: ['InternalAccounts'], awaitRefetchQueries: true };
  const [admitStaff] = useMutation(ADMIT_STAFF, refetch);
  const [assignRole] = useMutation(ASSIGN_PLATFORM_ROLE);
  const [unassignRole] = useMutation(UNASSIGN_PLATFORM_ROLE);
  const [removeStaff] = useMutation(REMOVE_STAFF, refetch);
  const portfolios = useQuery<PortfoliosResult>(PORTFOLIOS_QUERY, {
    skip: !canAssignBrokerages,
    fetchPolicy: 'cache-and-network',
  });
  const [assignManager] = useMutation(ASSIGN_TENANT_MANAGER);
  const [unassignManager] = useMutation(UNASSIGN_TENANT_MANAGER);

  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<InternalAccount | null>(null);
  const [removing, setRemoving] = useState<InternalAccount | null>(null);
  const [assigning, setAssigning] = useState<InternalAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canRead) {
    return (
      <AccessNotice area="people and access" onRetry={recheckAccess} state={access as 'checking'} />
    );
  }

  const meId = me.data?.me.id ?? null;
  const meEmail = identity?.email ?? null;
  const label = (account: InternalAccount) => personLabel(account, meId, meEmail);

  const run = async (call: () => Promise<unknown>, success: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await call();
      toast.success({ title: success });
      return true;
    } catch (cause) {
      setError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        recheckAccess();
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onInvite = async ({ email, roles }: { email: string; roles: PlatformRole[] }) => {
    if (await run(() => admitStaff({ variables: { email, roles } }), `${email} can now sign in`)) {
      setInviting(false);
    }
  };

  const onSaveRoles = async (roles: PlatformRole[]) => {
    if (!editing?.personId) {
      return;
    }
    const personId = editing.personId;
    const { add, remove } = roleChanges(editing.roles, roles);
    const ok = await run(async () => {
      try {
        // Additions first, so swapping one Admin role for another never leaves none.
        for (const role of add) {
          await assignRole({ variables: { personId, role } });
        }
        for (const role of remove) {
          await unassignRole({ variables: { personId, role } });
        }
      } finally {
        await accounts.refetch();
      }
    }, 'Roles updated');
    if (ok) {
      setEditing(null);
    }
  };

  const onRemove = async () => {
    if (!removing?.personId) {
      return;
    }
    const personId = removing.personId;
    if (await run(() => removeStaff({ variables: { personId } }), `${label(removing)} removed`)) {
      setRemoving(null);
    }
  };

  const managersOf = (personId: string) => {
    const held: Record<string, PortfolioAccess> = {};
    for (const tenant of portfolios.data?.managedTenants ?? []) {
      const row = tenant.managers.find((manager) => manager.personId === personId);
      if (row) {
        held[tenant.id] = portfolioAccessOf(row.grants);
      }
    }
    return held;
  };

  const brokerageCounts: Record<string, number> = {};
  for (const tenant of portfolios.data?.managedTenants ?? []) {
    for (const manager of tenant.managers) {
      if (manager.personId) {
        brokerageCounts[manager.personId] = (brokerageCounts[manager.personId] ?? 0) + 1;
      }
    }
  }

  const onSaveBrokerages = async (changes: PortfolioChange[]) => {
    if (!assigning?.personId) {
      return;
    }
    const personId = assigning.personId;
    const ok = await run(async () => {
      try {
        for (const { tenantId, access: wanted } of changes) {
          if (wanted === 'NONE') {
            await unassignManager({ variables: { tenantId, personId } });
          } else {
            await assignManager({
              variables: { tenantId, personId, grants: portfolioGrants(wanted) },
            });
          }
        }
      } finally {
        await portfolios.refetch();
      }
    }, 'Brokerages updated');
    if (ok) {
      setAssigning(null);
    }
  };

  const rows = accounts.data?.internalAccounts ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          canEdit ? (
            <Button
              icon={<Icon name="plus" />}
              onClick={() => {
                setError(null);
                setInviting(true);
              }}
            >
              Invite someone
            </Button>
          ) : null
        }
        lede="Who can use the internal portal, and what each person can change."
        title="People & access"
      />

      {access === 'read' ? (
        <div className={styles.notice}>
          <Callout title="You can see who has access, but not change it">
            Only admins can invite people or change roles.
          </Callout>
        </div>
      ) : null}

      {accounts.error && !accounts.data ? (
        <Callout tone="danger" title="People couldn't be loaded">
          {friendlyError(accounts.error)}
        </Callout>
      ) : accounts.loading && !accounts.data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading people…
        </p>
      ) : (
        <PeopleList
          accounts={rows}
          brokerageCounts={canAssignBrokerages ? brokerageCounts : undefined}
          onBrokerages={
            canAssignBrokerages
              ? (account) => {
                  setError(null);
                  setAssigning(account);
                }
              : undefined
          }
          canEdit={canEdit}
          meEmail={meEmail}
          meId={meId}
          onEditRoles={(account) => {
            setError(null);
            setEditing(account);
          }}
          onRemove={(account) => {
            setError(null);
            setRemoving(account);
          }}
        />
      )}

      <section aria-labelledby="roles-guide" className={styles.guide}>
        <h2 className={styles.guideTitle} id="roles-guide">
          What each role can do
        </h2>
        <dl className={styles.guideList}>
          {ROLES.map(({ role, name, summary }) => (
            <div className={styles.guideItem} key={role}>
              <dt>{name}</dt>
              <dd>{summary}</dd>
            </div>
          ))}
        </dl>
      </section>

      {inviting ? (
        <InviteForm
          busy={busy}
          error={error}
          onCancel={() => setInviting(false)}
          onSubmit={onInvite}
        />
      ) : null}

      {editing ? (
        <RolesForm
          busy={busy}
          current={editing.roles}
          error={error}
          onCancel={() => setEditing(null)}
          onSubmit={onSaveRoles}
          person={label(editing)}
        />
      ) : null}

      {assigning ? (
        <PortfolioForm
          brokerages={portfolios.data?.brokerages ?? []}
          busy={busy}
          current={assigning.personId ? managersOf(assigning.personId) : {}}
          error={error}
          onCancel={() => setAssigning(null)}
          onSubmit={onSaveBrokerages}
          person={label(assigning)}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          busy={busy}
          confirmLabel="Remove access"
          error={error}
          onCancel={() => setRemoving(null)}
          onConfirm={onRemove}
          title={`Remove ${label(removing)}?`}
        >
          They lose every role and can no longer use the internal portal. You can invite them again
          later.
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
